import React from 'react';
import { Chip, Icon, LinearProgress, TablePagination, TextField, Tooltip } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import MedicationService from '../../hooks/MedicationService';
import {
  MEDICATION_ANIMAL_TYPES,
  Medication,
  MedicationSubstance,
  MedicationWithdrawalPeriod
} from '../../models/medication';
import { AnimalType } from '../../models/animal';
import { getAnimalTypeName } from '../../utils/animalutils';
import { extractApiErrorMessage, notifications } from '../../utils';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE } from '../../constants/constants';

const service = new MedicationService();

/**
 * The sourced medication catalogue, read-only.
 *
 * Read-only on purpose: the catalogue is loaded from the register by an import, and a value a
 * veterinarian could overtype here would be silently replaced the next time it runs.
 */
const MedicationCatalogue: React.FC = () => {
  const { t } = useTranslation();

  const [medications, setMedications] = React.useState<Medication[]>([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [appliedSearch, setAppliedSearch] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [expanded, setExpanded] = React.useState<string | null>(null);

  // Debounced so a search does not fire a request per keystroke.
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedSearch(searchTerm);
      setPage(0);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  React.useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);

      try {
        const result = await service.getMedications(appliedSearch, page + 1, rowsPerPage);

        if (!cancelled) {
          setMedications(result.items ?? []);
          setTotalCount(result.totalCount ?? 0);
        }
      } catch (error) {
        if (!cancelled) {
          notifications.error(extractApiErrorMessage(error, { fallback: t('f-medications-load-failed') }));
          setMedications([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [appliedSearch, page, rowsPerPage, t]);

  /**
   * The rows for one life stage. More than one is normal: the register can state a period for a
   * specific group and another for a row it did not split, and both apply.
   */
  const periodsFor = (medication: Medication, animalType: AnimalType): MedicationWithdrawalPeriod[] =>
    medication.withdrawalPeriods.filter((period) => period.animalType === animalType);

  /**
   * `100 mg/ml`. The register writes the pack unit as `cc/ml`, which reads as a typo beside a
   * strength, so it is shown the way a label would print it.
   */
  const substanceStrength = (substance: MedicationSubstance): string | null => {
    if (substance.amount === null || substance.amount === undefined) {
      return null;
    }

    const per = substance.dosageForm === 'cc/ml' ? 'ml' : substance.dosageForm;
    const amount = `${substance.amount} ${substance.unit ?? ''}`.trim();

    return per ? `${amount}/${per}` : amount;
  };

  /**
   * The register describes UDD, UDA and VRIJ itself, in Dutch; those three keys are that prose
   * translated. It answers NR and URA with the code repeated, so NR comes from the FIDIN glossary
   * and URA says plainly that nothing defines it.
   */
  const channellingHelp = (channelling: string): string => {
    const key = `f-medications-channelling-${channelling.toLowerCase()}`;
    const help = t(key);

    return help === key ? t('f-medications-channelling-help') : help;
  };

  /**
   * MUI's Icon clips its glyph when the font size is overridden - it keeps `overflow: hidden` over a
   * 1em box - and a flex row stretches it. `fontSize="small"` leaves the box alone and `shrink-0`
   * keeps the row from squeezing it.
   */
  const complexMarker = () => (
    <Tooltip title={t('f-medications-complex-help')}>
      <Icon fontSize="small" className="!text-[18px] text-amber-600 shrink-0 cursor-help">
        info
      </Icon>
    </Tooltip>
  );

  const renderSubstances = (medication: Medication) => {
    if (medication.substances.length === 0) {
      return <span className="text-gray-400">-</span>;
    }

    return (
      <div className="flex flex-col gap-0.5">
        {medication.substances.map((substance) => {
          const strength = substanceStrength(substance);

          return (
            <div key={substance.id} className="leading-tight">
              <span>{substance.name}</span>
              {strength && <span className="text-gray-500"> {strength}</span>}
            </div>
          );
        })}
      </div>
    );
  };

  const renderWithdrawalCell = (medication: Medication, animalType: AnimalType) => {
    const periods = periodsFor(medication, animalType);

    if (periods.length === 0) {
      return (
        <Tooltip title={t('f-medications-withdrawal-unestablished-help') as string}>
          <span className="text-amber-700 font-medium">{t('f-medications-withdrawal-unestablished')}</span>
        </Tooltip>
      );
    }

    return (
      <div className="flex flex-col gap-1">
        {periods.map((period) => (
          // flex-wrap, so the group label drops to its own line rather than breaking "35 days" in two.
          <div key={period.id} className="flex flex-wrap items-center gap-x-1">
            {period.days === null || period.days === undefined ? (
              <Tooltip title={period.daysRaw || (t('f-medications-withdrawal-unestablished-help') as string)}>
                <span className="text-amber-700 font-medium whitespace-nowrap cursor-help">
                  {t('f-medications-withdrawal-unestablished')}
                </span>
              </Tooltip>
            ) : (
              <span className="font-medium whitespace-nowrap">{t('f-medications-days', { count: period.days })}</span>
            )}

            {period.isComplex && complexMarker()}

            {periods.length > 1 && (
              <span className="text-xs text-gray-500 whitespace-nowrap">({period.sourceGroupLabel})</span>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderPacks = (medication: Medication) => (
    <div className="bg-gray-50 px-4 py-3">
      <div className="text-xs font-semibold text-gray-600 uppercase mb-2">
        {t('f-medications-packs', { count: medication.packs.length })}
      </div>

      <table className="w-full text-sm">
        <thead className="text-gray-600">
          <tr>
            <th className="px-3 py-2 text-left font-semibold">{t('f-medications-pack-name')}</th>
            <th className="px-3 py-2 text-left font-semibold">{t('f-medications-registration-number')}</th>
            <th className="px-3 py-2 text-left font-semibold">{t('f-medications-packaging')}</th>
            <th className="px-3 py-2 text-left font-semibold">{t('f-medications-ean')}</th>
            <th className="px-3 py-2 text-left font-semibold">{t('f-medications-producer')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {medication.packs.map((pack) => (
            <tr key={pack.id}>
              <td className="px-3 py-2">{pack.productName}</td>
              <td className="px-3 py-2">{pack.registrationNumber}</td>
              <td className="px-3 py-2">{pack.packaging || '-'}</td>
              <td className="px-3 py-2 font-mono text-xs">{pack.eanCode || '-'}</td>
              <td className="px-3 py-2">{pack.producer || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-1">
        <h1 className="text-2xl font-semibold text-gray-900">{t('f-medications-title')}</h1>
        <Chip size="small" label={t('f-beta')} className="!bg-purple-100 !text-purple-800 !font-semibold" />
      </div>

      <p className="text-sm text-gray-600 mb-5 max-w-3xl">{t('f-medications-subtitle')}</p>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center gap-4">
          <TextField
            size="small"
            variant="outlined"
            className="!w-96"
            placeholder={t('f-medications-search-placeholder')}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />

          <span className="text-sm text-gray-500">{t('f-medications-count', { count: totalCount })}</span>
        </div>

        {loading && <LinearProgress />}

        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-gray-700 w-8" />
              <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('f-medications-short-name')}</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('f-medications-official-name')}</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('f-medications-substances')}</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">
                <Tooltip title={t('f-medications-channelling-help')}>
                  <span className="inline-flex items-center gap-1 cursor-help">
                    {t('f-medications-channelling')}
                    <Icon fontSize="small" className="!text-[16px] text-gray-400 shrink-0">
                      info
                    </Icon>
                  </span>
                </Tooltip>
              </th>
              {MEDICATION_ANIMAL_TYPES.map((animalType) => (
                <th key={animalType} className="px-4 py-3 text-left font-semibold text-gray-700">
                  {getAnimalTypeName(animalType, t)}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {medications.map((medication) => (
              <React.Fragment key={medication.id}>
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      aria-label={t('f-medications-packs', { count: medication.packs.length })}
                      onClick={() => setExpanded(expanded === medication.id ? null : medication.id)}
                    >
                      <Icon className="!text-lg text-gray-500">
                        {expanded === medication.id ? 'expand_less' : 'expand_more'}
                      </Icon>
                    </button>
                  </td>

                  <td className="px-4 py-3">
                    <div className="font-mono font-semibold">{medication.shortName}</div>
                    <div className="text-xs text-gray-500">
                      {t('f-medications-packs', { count: medication.packs.length })}
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <div>{medication.officialName}</div>
                    {medication.atcCodes && <div className="text-xs text-gray-500">{medication.atcCodes}</div>}
                  </td>

                  <td className="px-4 py-3">{renderSubstances(medication)}</td>

                  <td className="px-4 py-3">
                    {medication.channelling ? (
                      <Tooltip title={channellingHelp(medication.channelling)}>
                        <Chip
                          size="small"
                          label={medication.channelling}
                          className={
                            medication.channelling === 'UDD'
                              ? '!bg-orange-100 !text-orange-800 !cursor-help'
                              : '!bg-gray-100 !text-gray-700 !cursor-help'
                          }
                        />
                      </Tooltip>
                    ) : (
                      '-'
                    )}
                  </td>

                  {MEDICATION_ANIMAL_TYPES.map((animalType) => (
                    <td key={animalType} className="px-4 py-3">
                      {renderWithdrawalCell(medication, animalType)}
                    </td>
                  ))}
                </tr>

                {expanded === medication.id && (
                  <tr>
                    <td colSpan={5 + MEDICATION_ANIMAL_TYPES.length} className="p-0">
                      {renderPacks(medication)}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}

            {!loading && medications.length === 0 && (
              <tr>
                <td colSpan={5 + MEDICATION_ANIMAL_TYPES.length} className="px-4 py-10 text-center text-gray-500">
                  {t('f-medications-empty')}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
          onPageChange={(_, next) => setPage(next)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </div>
    </div>
  );
};

export default MedicationCatalogue;
